import { useState } from 'react'

import { domainLocales } from '@hhgtech/hhg-components/constants'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Stack, Box, Flex, rem, useMantineTheme } from '@mantine/core'
import { useClickOutside } from '@mantine/hooks'
import Link from 'next/link'
import { useIntl } from 'react-intl'

import ArrowDown from 'components/atoms/icons/ArrowDown'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { Spotlight } from 'interfaces/types'

const Breadcrumb = ({ spotlight }: { spotlight: Spotlight }) => {
  const { asPath, locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()
  const ref = useClickOutside(() => setOpenFakeDropdown(false))
  const theme = useMantineTheme()
  const [openFakeDropdown, setOpenFakeDropdown] = useState(false)
  const prefix = asPath.startsWith('/spotlight/') ? '/spotlight/' : '/'
  const options = (spotlight?.listGroup || []).map((i) => ({
    label: i.name,
    icon: i.icon,
    value: i.groupId,
    url: `${prefix}${spotlight.postName}/${i.groupId}`,
  }))
  const selected =
    options.filter((i) => i.value === spotlight.groupIdActive)?.[0] || {}
  return spotlight.listGroup.length > 1 ? (
    <Box
      ref={ref}
      sx={(theme) => ({
        position: 'relative',
        img: {
          width: rem(24),
        },
        userSelect: 'none',
        cursor: 'pointer',
        [theme.fn.smallerThan('sm')]: {
          marginLeft: rem(-16),
          marginRight: rem(-16),
          paddingLeft: rem(48),
          paddingRight: rem(40),
          paddingTop: rem(10),
          paddingBottom: rem(10),
        },
        [theme.fn.largerThan('sm')]: {
          display: 'inline-block',
          paddingLeft: rem(32),
          paddingRight: rem(24),
          paddingTop: rem(16),
          paddingBottom: rem(16),
        },
      })}
      data-event-category="Service Connection"
      data-event-action="Curated for Audience Type Click"
      data-event-label={currentUrl}
      className="pointer-event-child-none"
      onClick={() => setOpenFakeDropdown(!openFakeDropdown)}
    >
      <Flex
        align={'center'}
        sx={(theme) => ({
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          [theme.fn.smallerThan('sm')]: {
            left: rem(16),
          },
        })}
      >
        <img
          src={`${selected.icon}?w=80&q=90`}
          style={{ borderRadius: 'inherit' }}
          alt=""
          loading="lazy"
        />
      </Flex>
      <Flex
        align={'center'}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          [theme.fn.smallerThan('sm')]: {
            right: rem(16),
          },
        }}
      >
        <ArrowDown size={20} color={theme.colors.gray[6]} />
      </Flex>
      <Box
        sx={(theme) => ({
          [theme.fn.smallerThan('sm')]: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        })}
      >
        <Text size="p1" as="span">
          {f({
            id: 'carePath.audienceBreadcrumb.label',
            defaultMessage: 'Curated for',
          })}
        </Text>
        <Text
          size="s2"
          as="span"
          sx={(theme) => ({
            marginLeft: rem(8),
            [theme.fn.largerThan('sm')]: {
              position: 'relative',
            },
          })}
        >
          {selected?.label}
          <Box
            sx={(theme) => ({
              display: openFakeDropdown ? 'block' : 'none',
              position: 'absolute',
              left: 0,
              top: '100%',
              whiteSpace: 'nowrap',
              background: '#fff',
              border: `1px solid ${theme.colors.gray[1]}`,
              zIndex: 1,
              padding: rem(2),
              boxShadow: '1px 1px 4px 1px rgba(0,0,0,0.05)',
              '*': {
                pointerEvents: 'auto',
              },
              [theme.fn.smallerThan('sm')]: {
                width: '100%',
              },
              [theme.fn.largerThan('sm')]: {
                borderRadius: rem(4),
                top: 'calc(100% + 8px)',
              },
            })}
          >
            <Stack spacing={0}>
              {options
                .filter((i) => i.value !== spotlight.groupIdActive)
                .map((i, idx) => {
                  return (
                    <Link
                      prefetch={false}
                      href={i.url}
                      passHref
                      key={String(idx)}
                      legacyBehavior
                    >
                      <Box
                        data-event-category="Service Connection"
                        data-event-action={`Curated for ${i.label} Click`}
                        data-event-label={`https://${domainLocales[locale]}${i.url}`}
                        component="a"
                        key={String(idx)}
                        sx={(theme) => ({
                          display: 'flex',
                          alignItems: 'center',
                          gap: rem(8),
                          padding: `${rem(8)} ${rem(16)}`,
                          cursor: 'pointer',
                          '&:hover': {
                            background: theme.colors.gray[1],
                          },
                          img: {
                            maxWidth: rem(24),
                            maxHeight: rem(24),
                          },
                          '*': {
                            pointerEvents: 'none',
                          },
                        })}
                        onClick={() => {
                          setOpenFakeDropdown(false)
                        }}
                      >
                        <img
                          src={`${i.icon}?w=80&q=90`}
                          style={{ borderRadius: 'inherit' }}
                          alt=""
                          loading="lazy"
                        />
                        <Text size={isMobile ? 'p1' : 'p3'}>{i.label}</Text>
                      </Box>
                    </Link>
                  )
                })}
            </Stack>
          </Box>
        </Text>
      </Box>
    </Box>
  ) : null
}
export { Breadcrumb }
